import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TokenResponse } from '../models/auth.models';

/**
 * Shared in-flight refresh, so concurrent requests that all 401 trigger a
 * single /auth/refresh and then retry once with the new access token.
 * Reset to null once the refresh settles.
 */
let refreshInFlight$: Observable<TokenResponse> | null = null;

/** login/refresh/logout take no bearer and must not trigger refresh-on-401. */
function isAuthEndpoint(url: string): boolean {
  return /\/auth\/(login|refresh|logout)(\/|\?|$)/.test(url);
}

function attachToken(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return token
    ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
    : req;
}

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  refreshInFlight$ ??= auth.refresh().pipe(
    shareReplay(1),
    catchError(err => {
      refreshInFlight$ = null;
      auth.clearSession();
      router.navigate(['/login']);
      return throwError(() => err);
    }),
  );

  return refreshInFlight$.pipe(
    take(1),
    switchMap(res => {
      refreshInFlight$ = null;
      return next(attachToken(req, res.access_token));
    }),
  );
}

export const tokenInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Auth endpoints: pass through untouched.
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  const authReq = attachToken(req, auth.getAccessToken());

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        // Token missing/expired — try a single refresh + retry, else re-login.
        return handle401(authReq, next, auth, router);
      }
      if (error.status === 403) {
        // Valid token, but the account's role is not permitted for this endpoint.
        auth.clearSession();
        router.navigate(['/login'], { queryParams: { error: 'forbidden' } });
      }
      return throwError(() => error);
    }),
  );
};
